import { useEffect, useRef, useState } from "react";

interface HeroSectionProps {
  onCtaClick: () => void;
}

export default function HeroSection({ onCtaClick }: HeroSectionProps) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section
      ref={ref}
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
      style={{ minHeight: "100vh" }}
    >
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/5adb63eaff248806410153bce6b8f9e1.jpg"
          alt="Luxury med spa"
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/50 to-black/70" />

      {/* Hero Content */}
      <div
        className="relative z-10 w-full max-w-4xl mx-auto px-6 text-center"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(32px)",
          transition: "opacity 0.9s ease, transform 0.9s ease",
        }}
      >
        <span
          className="inline-block text-xs font-semibold tracking-widest uppercase mb-6 px-4 py-1.5 rounded-full"
          style={{
            background: "rgba(201, 149, 111, 0.25)",
            color: "#e8c4a8",
            border: "1px solid rgba(201, 149, 111, 0.4)",
          }}
        >
          Free 15-Minute Growth Audit
        </span>

        <h1
          className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-8"
          style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.02em" }}
        >
          Is Your Med Spa Missing{" "}
          <span style={{ color: "#C9956F" }}>20–40 New Clients</span> Every
          Month?
        </h1>

        <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
          Discover exactly what&apos;s holding your growth back and get a
          clear roadmap to fix it, completely free.
        </p>

        <button
          onClick={onCtaClick}
          className="whitespace-nowrap inline-flex items-center gap-2 text-white font-semibold text-base px-10 py-4 rounded-full cursor-pointer transition-all duration-300 hover:scale-105 active:scale-95"
          style={{
            background: "linear-gradient(135deg, #C9956F 0%, #B07048 100%)",
            boxShadow: "0 8px 32px rgba(180, 100, 55, 0.45)",
          }}
        >
          Claim My Free Audit
          <i className="ri-arrow-right-line text-lg" />
        </button>

        <p className="text-white/45 text-sm mt-4" style={{ fontFamily: "'Inter', sans-serif" }}>
          No obligations. No credit card. Just clarity.
        </p>
      </div>

      {/* Scroll indicator */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
        style={{
          opacity: visible ? 0.6 : 0,
          transition: "opacity 1.2s ease 0.8s",
        }}
      >
        <span className="text-white/60 text-xs tracking-widest uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
          Scroll
        </span>
        <div className="w-px h-8 bg-white/40 animate-pulse" />
      </div>
    </section>
  );
}
